import {
  CoverageEvent,
  EstimatedCoverage,
  ProductCoverage,
} from "api/types";
import {
  useReactTable,
  createColumnHelper,
  SortingState,
  flexRender,
  getCoreRowModel,
  ColumnDef,
  Row,
  Cell,
  Header as TableHeader,
  Table as ReactTableType,
} from "@tanstack/react-table";
import {
  Box,
  Flex,
  Text,
  useColorModeValue,
  Icon,
  VStack,
  TextProps,
  Spinner,
} from "@chakra-ui/react";

import { MdCheckCircle, MdCancel, MdOutlineError, MdShoppingCart } from "react-icons/md";
import {
  FaShip,
  FaInfo,
  FaChartSimple,
  FaSortDown,
  FaSortUp,
  FaWarehouse,
} from "react-icons/fa6";
import { BsGraphUpArrow } from "react-icons/bs";
import { AiOutlineFundProjectionScreen } from "react-icons/ai";

import { useVirtualizer, type VirtualItem } from "@tanstack/react-virtual";

import {
  useState,
  useRef,
  CSSProperties,
  useCallback,
  useEffect,
  useMemo,
  memo,
  type ReactNode,
} from "react";
import { FaFlag } from "react-icons/fa";

import { ApiCoverageDeviationReasonPatch } from "api/coverage/coverageList";
import { DeviationReasonSelect } from "components/deviationReason/DeviationReasonSelect";
import { useDataTooltipDelegation } from "hooks/useDataTooltipDelegation";
import {
  PeriodDeviationReasonCell,
  type PeriodDeviationReasonCellProps,
} from "./PeriodDeviationReasonCell";

export const Header = (props: { text: string }) => {
  const { text } = props;
  return (
    <Text
      justifyContent="center"
      align="center"
      fontSize={{ sm: "10px", lg: "12px" }}
      color="gray.400"
      textAlign="center"
      w="100%"
    >
      {text}
    </Text>
  );
};

/** Umbral de filas a partir del cual la tabla usa virtualización de filas. */
export const COVERAGE_TABLE_VIRTUALIZE_THRESHOLD = 50;

/** Umbral de columnas desplazables (períodos) para virtualización horizontal. */
export const COVERAGE_TABLE_COLUMN_VIRTUALIZE_THRESHOLD = 12;

function resolveScrollableHeader(
  scrollableHeaders: TableHeader<ProductCoverage, unknown>[],
  scrollableColumns: ReturnType<ReactTableType<ProductCoverage>["getVisibleLeafColumns"]>,
  columnIndex: number,
): TableHeader<ProductCoverage, unknown> | undefined {
  const leafColumn = scrollableColumns[columnIndex];
  if (!leafColumn) {
    return undefined;
  }
  const byLeafId = scrollableHeaders.find((header) =>
    getVisibleLeafColumns(header.column).some((leaf) => leaf.id === leafColumn.id),
  );
  if (byLeafId) {
    return byLeafId;
  }
  return scrollableHeaders[columnIndex];
}

/** Ancho de columna de período (7 dígitos + selector de motivo). */
const COVERAGE_PERIOD_COLUMN_WIDTH = 260;

/** Ancho columna «Actual» (inv. inicial 7 dígitos + motivo). */
const COVERAGE_CURRENT_COLUMN_WIDTH = 200;

const COVERAGE_PERIOD_CELL_MIN_WIDTH = `${COVERAGE_PERIOD_COLUMN_WIDTH - 12}px`;

const COVERAGE_ROW_ESTIMATE = 184;
const COVERAGE_HEADER_ROW_HEIGHT = 38;
const COVERAGE_TABLE_BG = "#111c44";

const FLAT_GRID_CELL_STYLE: CSSProperties = {
  boxShadow: "none",
};

const COVERAGE_METRIC_TEXT_PROPS: TextProps = {
  fontSize: { base: "10px", lg: "12px" },
  fontWeight: "700",
  whiteSpace: "nowrap",
  sx: { fontVariantNumeric: "tabular-nums" },
};

const DataTooltipWrap = (props: { label: string; children: ReactNode }) => (
  <span data-tooltip={props.label}>{props.children}</span>
);

function getVisibleLeafColumns(
  column: {
    getLeafColumns: () => Array<{
      id: string;
      getIsVisible: () => boolean;
      getIsPinned: () => false | "left" | "right" | undefined;
    }>;
  },
) {
  return column.getLeafColumns().filter((leaf) => leaf.getIsVisible());
}

function isPinnedTableHeader(header: TableHeader<ProductCoverage, unknown>): boolean {
  const visibleLeafs = getVisibleLeafColumns(header.column);
  if (visibleLeafs.length === 0) {
    return header.column.getIsPinned() === "left";
  }
  return visibleLeafs.every((col) => col.getIsPinned() === "left");
}

function normalizeCoverageMetric(value: unknown): number | null {
  if (value === null || value === undefined) {
    return null;
  }
  const n = Number(value);
  return Number.isFinite(n) ? n : null;
}

function compareCoverageMetrics(a: unknown, b: unknown): number {
  const na = normalizeCoverageMetric(a);
  const nb = normalizeCoverageMetric(b);
  if (na === null && nb === null) {
    return 0;
  }
  if (na === null) {
    return 1;
  }
  if (nb === null) {
    return -1;
  }
  return na - nb;
}

function getCoverageSortValue(
  row: ProductCoverage,
  columnId: string,
): string | number | null {
  switch (columnId) {
    case "code":
      return row.productProps.erpCode ?? "";
    case "description":
      return row.productProps.description ?? "";
    case "category":
      return row.productProps.category ?? "";
    case "line":
      return row.productProps.line ?? "";
    case "subline":
      return row.productProps.subLine ?? "";
    case "fob-unidad":
      return row.productProps.costoFobXUnidad ?? "";
    case "fob-bulto":
      return row.productProps.costoFobXBulto ?? "";
    case "unidad-bulto":
      return row.productProps.unidadXBulto ?? null;
    case "cbm-bulto":
      return row.productProps.cbmXBulto ?? null;
    case "peso-bulto":
      return row.productProps.pesoXBulto ?? null;
    default:
      break;
  }

  const daysMatch = /^period-days-(\d+)$/.exec(columnId);
  if (daysMatch) {
    const periodIndex = Number(daysMatch[1]);
    return row.coverage[periodIndex]?.coverageTime ?? null;
  }

  const invMatch = /^period-inv-(\d+)$/.exec(columnId);
  if (invMatch) {
    const periodIndex = Number(invMatch[1]);
    return row.coverage[periodIndex]?.estimatedUnits ?? null;
  }

  const heatmapDaysMatch = /^days-(\d+)$/.exec(columnId);
  if (heatmapDaysMatch) {
    const periodIndex = Number(heatmapDaysMatch[1]);
    return row.coverage[periodIndex]?.coverageTime ?? null;
  }

  return null;
}

function compareCoverageRows(
  rowA: ProductCoverage,
  rowB: ProductCoverage,
  columnId: string,
): number {
  const a = getCoverageSortValue(rowA, columnId);
  const b = getCoverageSortValue(rowB, columnId);

  if (typeof a === "string" || typeof b === "string") {
    return String(a ?? "").localeCompare(String(b ?? ""), "es", {
      numeric: true,
      sensitivity: "base",
    });
  }

  return compareCoverageMetrics(a, b);
}

function sortCoverageRows(
  rows: ProductCoverage[],
  sorting: SortingState,
): ProductCoverage[] {
  if (sorting.length === 0) {
    return rows;
  }
  const indexed = rows.map((row, index) => ({ row, index }));
  indexed.sort((left, right) => {
    for (const sort of sorting) {
      const cmp = compareCoverageRows(left.row, right.row, sort.id);
      if (cmp !== 0) {
        return sort.desc ? -cmp : cmp;
      }
    }
    return left.index - right.index;
  });
  return indexed.map(({ row }) => row);
}

function toggleCoverageColumnSort(
  table: ReactTableType<ProductCoverage>,
  columnId: string,
): void {
  const current = table.getState().sorting;
  const existing = current.find((entry) => entry.id === columnId);
  if (!existing) {
    table.setSorting([{ id: columnId, desc: false }]);
    return;
  }
  if (!existing.desc) {
    table.setSorting([{ id: columnId, desc: true }]);
    return;
  }
  table.setSorting([]);
}

const PeriodColumnHeader = (props: {
  periodIndex: number;
  table: ReactTableType<ProductCoverage>;
}) => {
  const { periodIndex, table } = props;
  const daysColumnId = `period-days-${periodIndex}`;
  const invColumnId = `period-inv-${periodIndex}`;
  const sorting = table.getState().sorting;
  const daysSort = sorting.find((entry) => entry.id === daysColumnId);
  const invSort = sorting.find((entry) => entry.id === invColumnId);

  const renderSortableLabel = (
    label: string,
    columnId: string,
    activeSort: (typeof sorting)[number] | undefined,
  ) => (
    <Flex
      flex="1"
      justify="center"
      align="center"
      gap={1}
      cursor="pointer"
      onClick={(event) => {
        event.stopPropagation();
        toggleCoverageColumnSort(table, columnId);
      }}
    >
      <Text
        fontSize={{ sm: "10px", lg: "12px" }}
        color="gray.400"
        textAlign="center"
      >
        {label}
      </Text>
      {activeSort ? (
        activeSort.desc ? (
          <Icon as={FaSortDown} />
        ) : (
          <Icon as={FaSortUp} />
        )
      ) : null}
    </Flex>
  );

  return (
    <Flex
      w="100%"
      justify="space-between"
      align="center"
      px="4px"
      gap={1}
    >
      <Box w="36px" textAlign="center">
        <Icon as={FaInfo} color="gray.400" />
      </Box>
      {renderSortableLabel("Días", daysColumnId, daysSort)}
      {renderSortableLabel("Inv. Final", invColumnId, invSort)}
    </Flex>
  );
};

export const ProductValue = memo(function ProductValue(props: { attr: string;[x: string]: any }) {
  const { attr } = props;
  const textColor = useColorModeValue("secondaryGray.900", "white");
  return (
    <Flex align="center" justify="center" w="100%">
      <Text
        color={textColor}
        fontSize={{ base: "10px", lg: "12px" }}
        fontWeight="700"
        w="100%"
        textAlign="center"
        wordBreak="break-word"
        lineHeight="short"
      >
        {attr}
      </Text>
    </Flex>
  );
});

const CoverageValue = memo(function CoverageValue(props: {
  value: number | undefined | null;
  textProps?: TextProps;
}) {
  const { value, textProps } = props;
  const invalid =
    value === undefined ||
    value === null ||
    (typeof value === "number" && Number.isNaN(value));
  const textColor = useColorModeValue("secondaryGray.900", "white");
  let _textProps: TextProps = {
    fontSize: { base: "11px", lg: "13px" },
    fontWeight: "700",
  };
  if (textProps) {
    _textProps = textProps;
  }
  let _v: string;
  if (invalid) {
    _v = "—";
  } else if (value === -30 || value === -7) {
    _v = "0";
  } else if (value === -1650 || value === -385) {
    _v = "S.F";
  } else {
    _v = (value as number).toLocaleString("defaults", {
      minimumFractionDigits: 0,
    });
  }

  return (
    <Flex align="center" justify={_textProps.textAlign === "right" ? "flex-end" : "center"}>
      <Text
        color={textColor}
        w="100%"
        textAlign="center"
        whiteSpace="nowrap"
        sx={{ fontVariantNumeric: "tabular-nums" }}
        {..._textProps}
      >
        {_v}
      </Text>
    </Flex>
  );
});

function promVentasThreeMonthsAverage(
  coverage: EstimatedCoverage[],
): number {
  const take = (i: number): number => {
    const v = coverage[i]?.current?.forecast;
    return typeof v === "number" && Number.isFinite(v) ? v : 0;
  };
  return (take(0) + take(1) + take(2)) / 3;
}

function formatBultoLabel(
  v: number | string | null | undefined,
  fractionDigits = 3,
): string {
  if (v === null || v === undefined || v === "") {
    return "—";
  }
  const n = typeof v === "number" ? v : Number(v);
  if (!Number.isFinite(n)) {
    return "—";
  }
  return n.toLocaleString("es", {
    minimumFractionDigits: 0,
    maximumFractionDigits: fractionDigits,
  });
}

function formatAttrLabel(v: string | null | undefined): string {
  if (v === null || v === undefined || v === "") {
    return "—";
  }
  return v;
}

const isMotivoAnchorColumn = (columnId: string): boolean =>
  columnId === "current" || columnId.startsWith("period-");

const DeviationReasonEditor = (props: {
  productId: number;
  value: string;
  valueLabel?: string | null;
  canEdit: boolean;
  onSaved: (productId: number, deviationNoteId: string, label: string) => void;
}) => {
  const { productId, value, valueLabel, canEdit, onSaved } = props;
  const [local, setLocal] = useState(value);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    setLocal(value);
  }, [value]);

  const persist = async (noteId: string, label: string) => {
    if (!canEdit || noteId === value) {
      return;
    }
    setSaving(true);
    try {
      await ApiCoverageDeviationReasonPatch(productId, label);
      onSaved(productId, noteId, label);
    } catch {
      setLocal(value);
    } finally {
      setSaving(false);
    }
  };

  const handleChange = (noteId: string, label?: string) => {
    setLocal(noteId);
    void persist(noteId, label ?? noteId);
  };

  return (
    <VStack align="stretch" spacing={1} w="100%">
      <Text fontSize="9px" color="gray.500">
        Motivo de desviación
      </Text>
      <Flex align="center" gap="6px" w="100%" minW={0}>
        <DeviationReasonSelect
          value={local}
          selectedLabel={valueLabel}
          onChange={handleChange}
          isDisabled={!canEdit || saving}
          placeholder="Elegir…"
          size="sm"
          optionValue="id"
        />
        {saving ? <Spinner size="xs" flexShrink={0} /> : null}
      </Flex>
    </VStack>
  );
};

/** Columna «Actual»: inv. inicial, promedio 3M, días de inventario, motivo editable. */
const ActualColumnCell = memo(function ActualColumnCell(props: {
  row: ProductCoverage;
  canEditDeviationReason: boolean;
  getProductGeneralNoteId?: (productId: number) => string | null;
  getProductGeneralNoteLabel?: (productId: number) => string | null;
  onDeviationReasonSaved?: (
    productId: number,
    deviationNoteId: string,
    label: string,
  ) => void;
}) {
  const {
    row,
    canEditDeviationReason,
    getProductGeneralNoteId,
    getProductGeneralNoteLabel,
    onDeviationReasonSaved,
  } = props;
  const emptyStateColor = useColorModeValue("secondaryGray.600", "gray.400");
  const diasTextColor = useColorModeValue("secondaryGray.900", "white");
  const cur = row.coverage[0]?.current;
  const inv = cur?.inventory;
  const sales = cur?.sales;
  const forecast = cur?.forecast;
  const promVentas = promVentasThreeMonthsAverage(row.coverage);
  const invOk = typeof inv === "number" && Number.isFinite(inv);
  const salesOk = typeof sales === "number" && Number.isFinite(sales);
  const forecastOk =
    typeof forecast === "number" && Number.isFinite(forecast) && forecast > 0;
  const coberturaMeses =
    invOk && forecastOk ? inv! / forecast! : null;
  const coberturaDias =
    coberturaMeses !== null && Number.isFinite(coberturaMeses)
      ? Math.round(coberturaMeses * 30)
      : null;

  if (!row.coverage?.length) {
    return (
      <Flex align="center" justify="center" w="100%" minH="96px" px="4px">
        <Text
          fontSize={{ base: "11px", lg: "12px" }}
          color={emptyStateColor}
          textAlign="center"
        >
          Sin datos actuales
        </Text>
      </Flex>
    );
  }

  return (
    <Flex
      direction="column"
      w="100%"
      minW={0}
      maxW="100%"
      h="100%"
      minH={COVERAGE_ROW_ESTIMATE - 8}
      px="2px"
      py={1}
      boxSizing="border-box"
    >
      <Box flexShrink={0} w="100%">
        <VStack align="stretch" spacing={1} w="100%">
          <Flex justifyContent="space-between" w="100%" gap={2} align="center">
            <DataTooltipWrap label="Inv. Inicial">
              <Icon as={FaWarehouse} flexShrink={0} />
            </DataTooltipWrap>
            <Box flex="1" minW={0} textAlign="right">
              <CoverageValue
                value={invOk ? inv! : undefined}
                textProps={{
                  ...COVERAGE_METRIC_TEXT_PROPS,
                  fontWeight: "600",
                  textAlign: "right",
                }}
              />
            </Box>
          </Flex>
          <Flex justifyContent="space-between" w="100%" gap={2} align="center">
            <DataTooltipWrap label="Ventas">
              <Icon as={MdShoppingCart} flexShrink={0} />
            </DataTooltipWrap>
            <Box flex="1" minW={0} textAlign="right">
              <CoverageValue
                value={salesOk ? Math.round(sales!) : undefined}
                textProps={{
                  ...COVERAGE_METRIC_TEXT_PROPS,
                  fontWeight: "600",
                  textAlign: "right",
                }}
              />
            </Box>
          </Flex>
          <Flex justifyContent="space-between" w="100%" gap={2} align="center">
            <DataTooltipWrap label="Prom. Ventas (3M)">
              <Icon as={BsGraphUpArrow} flexShrink={0} />
            </DataTooltipWrap>
            <Box flex="1" minW={0} textAlign="right">
              <CoverageValue
                value={Math.round(promVentas)}
                textProps={{
                  ...COVERAGE_METRIC_TEXT_PROPS,
                  fontWeight: "600",
                  textAlign: "right",
                }}
              />
            </Box>
          </Flex>
          <Flex justifyContent="space-between" w="100%" gap={2} align="center">
            <DataTooltipWrap label="Cobertura">
              <Icon as={FaChartSimple} flexShrink={0} />
            </DataTooltipWrap>
            <Text
              color={diasTextColor}
              fontSize={{ base: "10px", lg: "12px" }}
              fontWeight="600"
              textAlign="right"
              flex="1"
              minW={0}
              whiteSpace="nowrap"
              sx={{ fontVariantNumeric: "tabular-nums" }}
            >
              {coberturaDias === null ||
                coberturaMeses === null ||
                !Number.isFinite(coberturaMeses)
                ? "—"
                : `${coberturaDias.toLocaleString("es")} / ${Math.round(coberturaMeses).toLocaleString("es")}`}
            </Text>
          </Flex>
        </VStack>
      </Box>
      <Box
        mt="auto"
        flexShrink={0}
        w="100%"
        pt={2}
        borderTopWidth="1px"
        borderTopColor="whiteAlpha.200"
      >
        <DeviationReasonEditor
          productId={row.productProps.id}
          value={getProductGeneralNoteId?.(row.productProps.id) ?? ""}
          valueLabel={getProductGeneralNoteLabel?.(row.productProps.id)}
          canEdit={canEditDeviationReason}
          onSaved={(id, noteId, label) =>
            onDeviationReasonSaved?.(id, noteId, label)
          }
        />
      </Box>
    </Flex>
  );
});

const CoverageEvents = memo(function CoverageEvents(props: { events: CoverageEvent }) {
  const { events } = props;
  let color = "green.500";
  let icon = MdCheckCircle;
  let message = "Correcto";
  if (events.alert) {
    if (events.alert.includes("warning")) {
      color = "orange.500";
      icon = MdOutlineError;
    }
    if (events.alert.includes("alert")) {
      color = "red.500";
      icon = MdCancel;
    }
    if (events.alert.includes("too_much_days")) {
      color = "purple.500";
      icon = MdCancel;
    }
    if (events.alert.includes("no_forecast")) {
      color = "blue.400"
      message = "Forecast no disponible.";
    }
    if (events.alert.includes("few_days_remaining")) {
      message = "Pocos días antes de quiebre.";
    }
    if (events.alert.includes("too_much_days")) {
      message = "Exceso de días detectado.";
    }
    if (events.alert.includes("shorted_units")) {
      message = "Quiebre detectado.";
    }
  }

  return (
    <Flex align="center">
      <VStack spacing={1} w="100%">
        <DataTooltipWrap label={message}>
          <Icon as={icon} color={color} />
        </DataTooltipWrap>
        {events.outcomes > 0 ? (
          <DataTooltipWrap
            label={`Venta estimada (Promediado): ${(
              events.outcomes as number
            ).toLocaleString("defaults", {
              minimumFractionDigits: 0,
            })}`}
          >
            <Icon as={FaChartSimple} color="gold" />
          </DataTooltipWrap>
        ) : null}
        {events.incomes.base > 0 ? (
          <DataTooltipWrap
            label={`Llegada: ${(events.incomes.base as number).toLocaleString(
              "defaults",
              {
                minimumFractionDigits: 0,
              },
            )}`}
          >
            <Icon as={FaShip} color={"blue.500"} />
          </DataTooltipWrap>
        ) : null}
        {events.incomes.simulation > 0 ? (
          <DataTooltipWrap
            label={`Llegada Simulada: ${(events.incomes.simulation as number).toLocaleString(
              "defaults",
              {
                minimumFractionDigits: 0,
              },
            )}`}
          >
            <Icon as={FaFlag} color={"blue.200"} />
          </DataTooltipWrap>
        ) : null}
      </VStack>
    </Flex>
  );
});

const PeriodCoverageCell = memo(function PeriodCoverageCell(props: {
  period: EstimatedCoverage | undefined;
  periodIndex: number;
  row: ProductCoverage;
  chronologyParameter: string;
  canEditPeriodDeviation: boolean;
  getPeriodDeviationNoteId?: (
    productId: number,
    periodIndex: number,
  ) => string | null;
  getPeriodDeviationNoteLabel?: (
    productId: number,
    periodIndex: number,
  ) => string | null;
  onPeriodDeviationChange?: PeriodDeviationReasonCellProps["onChange"];
}) {
  const {
    period,
    periodIndex,
    row,
    chronologyParameter,
    canEditPeriodDeviation,
    getPeriodDeviationNoteId,
    getPeriodDeviationNoteLabel,
    onPeriodDeviationChange,
  } = props;

  if (!period) {
    return (
      <Flex align="center" justify="center" minH="72px" w="100%">
        <Text fontSize="11px" color="gray.500">
          —
        </Text>
      </Flex>
    );
  }

  const canEditDeviation = Boolean(onPeriodDeviationChange);
  const storedNoteId =
    getPeriodDeviationNoteId?.(row.productProps.id, periodIndex) ?? null;
  const storedNoteLabel =
    getPeriodDeviationNoteLabel?.(row.productProps.id, periodIndex) ?? null;
  const hasStoredNote = Boolean(storedNoteId && storedNoteId.trim() !== "");
  const showMotivoFooter = canEditDeviation || hasStoredNote;

  return (
    <Flex
      direction="column"
      w="100%"
      maxW="100%"
      minW={0}
      h="100%"
      minH={COVERAGE_ROW_ESTIMATE - 8}
      px="2px"
      py={1}
      boxSizing="border-box"
    >
      <Box flexShrink={0} w="100%">
        <Flex align="center" justify="space-between" gap={1} w="100%" px={1}>
          <Flex align="center" justify="center" flex="0 0 32px">
            <CoverageEvents events={period.events} />
          </Flex>
          <Box flex="1" minW="58px" textAlign="center">
            <CoverageValue
              value={period.coverageTime}
              textProps={COVERAGE_METRIC_TEXT_PROPS}
            />
          </Box>
          <Box flex="1" minW="58px" textAlign="center">
            <CoverageValue
              value={period.estimatedUnits}
              textProps={COVERAGE_METRIC_TEXT_PROPS}
            />
          </Box>
        </Flex>
      </Box>
      {showMotivoFooter && onPeriodDeviationChange ? (
        <Box
          mt="auto"
          flexShrink={0}
          w="100%"
          pt={hasStoredNote || canEditDeviation ? 2 : 0}
          borderTopWidth={hasStoredNote || canEditDeviation ? "1px" : 0}
          borderTopColor="whiteAlpha.200"
        >
          <PeriodDeviationReasonCell
            key={`${row.productProps.id}-${periodIndex}-${storedNoteId ?? "empty"}`}
            productId={row.productProps.id}
            erpCode={row.productProps.erpCode}
            periodIndex={periodIndex}
            targetDate={period.targetDate}
            chronologyParameter={chronologyParameter}
            value={storedNoteId}
            valueLabel={storedNoteLabel}
            canEdit={canEditPeriodDeviation}
            onChange={onPeriodDeviationChange}
          />
        </Box>
      ) : null}
    </Flex>
  );
});

const getPinnedStickyStyle = (
  column: {
    getIsPinned: () => string | false | undefined;
    getStart: (position?: "left" | "right" | "center") => number;
    getSize: () => number;
  },
  _cellId: string,
): CSSProperties => {
  return {
    width: column.getSize(),
    minWidth: column.getSize(),
    maxWidth: column.getSize(),
    flexShrink: 0,
    boxSizing: "border-box",
  };
};

function getHeaderWidth(
  header: TableHeader<ProductCoverage, unknown>,
  pinnedPanelWidth: number,
): number {
  if (!isPinnedTableHeader(header)) {
    return header.getSize();
  }
  const visiblePinnedLeafs = getVisibleLeafColumns(header.column);
  if (visiblePinnedLeafs.length > 1 || header.colSpan > 1) {
    return pinnedPanelWidth;
  }
  return header.getSize();
}

function getScrollableHeaderWidth(
  header: TableHeader<ProductCoverage, unknown>,
  virtualColumn?: VirtualItem,
): number {
  if (virtualColumn) {
    return virtualColumn.size;
  }
  return header.getSize();
}

/** Texto del `<Header>` / Chakra `<Text>` por columna de período (evita «Invalid Date» en UI). */
const HEADER_DATE_FALLBACK = "—";

function coerceChronologyInt(value: unknown): number | null {
  if (value === null || value === undefined || value === "") {
    return null;
  }
  const n = Number(value);
  return Number.isFinite(n) ? Math.trunc(n) : null;
}

/** Encabezado de columna por período; evita `Invalid Date` si el API envía fechas incompletas o NaN. */
const getHeader = (
  info: EstimatedCoverage,
  chronologyParameter: string,
  short: boolean = false,
) => {
  const td = info.targetDate;
  if (!td) {
    return HEADER_DATE_FALLBACK;
  }
  if (chronologyParameter === "year_month") {
    const year = coerceChronologyInt(td.year);
    const month = coerceChronologyInt(td.month);
    if (
      year === null ||
      month === null ||
      month < 1 ||
      month > 12
    ) {
      return HEADER_DATE_FALLBACK;
    }
    const d = new Date(year, month - 1, 1);
    if (Number.isNaN(d.getTime())) {
      return HEADER_DATE_FALLBACK;
    }
    return d.toLocaleString("default", {
      year: "numeric",
      month: !short ? "long" : "short",
    });
  }
  const week = coerceChronologyInt(td.week);
  const year = coerceChronologyInt(td.year);
  const month = coerceChronologyInt(td.month);
  if (
    week === null ||
    year === null ||
    month === null ||
    month < 1 ||
    month > 12
  ) {
    return HEADER_DATE_FALLBACK;
  }
  return `${!short ? "Semana " : "S"}${week} (${year}${!short ? "-" + month : ""
    })`;
};

const DETAILED_PINNED_FIELDS = [
  "code",
  "description",
  "subline",
  "fob-unidad",
  "fob-bulto",
  "unidad-bulto",
  "cbm-bulto",
  "peso-bulto",
  "current",
] as const;

const HEATMAP_PINNED_FIELDS = ["code", "description", "subline"] as const;

const helper = createColumnHelper<ProductCoverage>();

type VirtualGridRowProps = {
  row: Row<ProductCoverage>;
  virtualRow: { index: number; start: number; size: number };
  virtualizeRows: boolean;
  virtualizeColumns: boolean;
  virtualColumns: VirtualItem[];
  scrollableWidth: number;
  pinnedWidth: number;
  renderCell: (cell: Cell<ProductCoverage, any>) => JSX.Element;
};

const VirtualGridRow = memo(function VirtualGridRow(props: VirtualGridRowProps) {
  const {
    row,
    virtualRow,
    virtualizeRows,
    virtualizeColumns,
    virtualColumns,
    scrollableWidth,
    pinnedWidth,
    renderCell,
  } = props;

  const cells = row.getVisibleCells();
  const pinnedCells = cells.filter((cell) => cell.column.getIsPinned());
  const scrollableCells = cells.filter((cell) => !cell.column.getIsPinned());

  return (
    <Box
      position={virtualizeRows ? "absolute" : "relative"}
      top={virtualizeRows ? `${virtualRow.start}px` : undefined}
      left={0}
      w={`${pinnedWidth + scrollableWidth}px`}
      minW={`${pinnedWidth + scrollableWidth}px`}
      h={`${virtualRow.size}px`}
      display="grid"
      gridTemplateColumns={`${pinnedWidth}px ${scrollableWidth}px`}
      alignItems="stretch"
      bg={COVERAGE_TABLE_BG}
      boxShadow="none"
      className="coverage-grid-row"
      overflow="visible"
    >
      <Box
        position="sticky"
        left={0}
        zIndex={4}
        bg={COVERAGE_TABLE_BG}
        w={`${pinnedWidth}px`}
        minW={`${pinnedWidth}px`}
        display="flex"
        alignItems="stretch"
        boxShadow="none"
        overflow="visible"
      >
        {pinnedCells.map((cell) => renderCell(cell))}
      </Box>
      <Box
        position="relative"
        w={`${scrollableWidth}px`}
        minW={`${scrollableWidth}px`}
        h="100%"
      >
        {virtualizeColumns ? (
          virtualColumns.map((virtualColumn) => {
            const cell = scrollableCells[virtualColumn.index];
            if (!cell) {
              return null;
            }
            return (
              <Box
                key={cell.id}
                position="absolute"
                top={0}
                left={`${virtualColumn.start}px`}
                w={`${virtualColumn.size}px`}
                h="100%"
              >
                {renderCell(cell)}
              </Box>
            );
          })
        ) : (
          <Flex h="100%" align="stretch">
            {scrollableCells.map((cell) => renderCell(cell))}
          </Flex>
        )}
      </Box>
    </Box>
  );
});

export const DataTable = (props: {
  data: Array<ProductCoverage>;
  virtualize: boolean;
  pinnedFields: readonly string[];
  columns: ColumnDef<ProductCoverage, any>[];
  renderCell: (cell: Cell<ProductCoverage, any>) => JSX.Element;
  scrollableColumnEstimate: number;
  setLoading: (value: boolean) => void;
}) => {
  const {
    data,
    virtualize,
    pinnedFields,
    columns,
    renderCell,
    scrollableColumnEstimate,
    setLoading,
  } = props;

  const [sorting, setSorting] = useState<SortingState>([]);
  const tableContainerRef = useRef<HTMLDivElement>(null);
  const bgColor = COVERAGE_TABLE_BG;
  const pinnedLimitField = pinnedFields[pinnedFields.length - 1] ?? "current";

  useDataTooltipDelegation(tableContainerRef);

  const sortedData = useMemo(
    () => sortCoverageRows(data, sorting),
    [data, sorting],
  );

  const table = useReactTable({
    data: sortedData,
    columns,
    initialState: {
      columnVisibility: {
        line: false,
        category: false,
      },
      columnPinning: {
        left: [...pinnedFields],
      },
    },
    state: {
      sorting,
    },
    onSortingChange: setSorting,
    manualSorting: true,
    getCoreRowModel: getCoreRowModel(),
    debugTable: false,
  });

  const { rows } = table.getRowModel();
  const headerGroups = table.getHeaderGroups();

  const scrollableColumns = useMemo(
    () => table.getVisibleLeafColumns().filter((col) => !col.getIsPinned()),
    [table, sortedData, sorting],
  );

  const virtualizeColumns =
    scrollableColumns.length > COVERAGE_TABLE_COLUMN_VIRTUALIZE_THRESHOLD;

  const columnVirtualizer = useVirtualizer({
    horizontal: true,
    count: virtualizeColumns ? scrollableColumns.length : 0,
    estimateSize: useCallback(
      (index: number) =>
        scrollableColumns[index]?.getSize() ?? scrollableColumnEstimate,
      [scrollableColumns, scrollableColumnEstimate],
    ),
    getScrollElement: useCallback(() => tableContainerRef.current, []),
    overscan: 2,
  });

  const virtualColumns = columnVirtualizer.getVirtualItems();
  const columnVirtualTotalSize = columnVirtualizer.getTotalSize();

  const pinnedWidth = useMemo(
    () =>
      table
        .getVisibleLeafColumns()
        .filter((col) => col.getIsPinned())
        .reduce((sum, col) => sum + col.getSize(), 0),
    [table, sortedData, sorting],
  );

  const scrollableWidth = virtualizeColumns
    ? columnVirtualTotalSize
    : scrollableColumns.reduce((sum, col) => sum + col.getSize(), 0);

  const rowVirtualizer = useVirtualizer({
    count: virtualize ? rows.length : 0,
    estimateSize: useCallback(() => COVERAGE_ROW_ESTIMATE, []),
    getScrollElement: useCallback(() => tableContainerRef.current, []),
    overscan: 6,
  });

  const renderHeaderCell = (
    header: TableHeader<ProductCoverage, unknown>,
    headerGroupIndex: number,
    virtualColumn?: VirtualItem,
  ) => {
    const columnId = String(header.column.id);
    const cellWidth = virtualColumn
      ? getScrollableHeaderWidth(header, virtualColumn)
      : getHeaderWidth(header, pinnedWidth);
    const stickyTop = headerGroupIndex === 0;
    return (
      <Box
        key={header.id}
        role="columnheader"
        flexShrink={0}
        px="5px"
        bg={bgColor}
        h={`${COVERAGE_HEADER_ROW_HEIGHT}px`}
        display="flex"
        alignItems="center"
        justifyContent="center"
        overflow="hidden"
        boxSizing="border-box"
        style={{
          width: cellWidth,
          minWidth: cellWidth,
          maxWidth: cellWidth,
          ...FLAT_GRID_CELL_STYLE,
        }}
        cursor={header.column.getCanSort() ? "pointer" : "default"}
        onClick={
          header.column.getCanSort()
            ? header.column.getToggleSortingHandler()
            : undefined
        }
      >
        <Flex
          justifyContent="center"
          align="center"
          fontSize={{ sm: "10px", lg: "12px" }}
          color="gray.400"
          w="100%"
          minW={0}
        >
          {flexRender(header.column.columnDef.header, header.getContext())}
          {{
            asc: <Icon as={FaSortUp} />,
            desc: <Icon as={FaSortDown} />,
          }[header.column.getIsSorted() as string] ?? null}
        </Flex>
      </Box>
    );
  };

  const renderHeaderGroupRow = (
    headerGroup: { id: string; headers: TableHeader<ProductCoverage, unknown>[] },
    headerGroupIndex: number,
  ) => {
    const pinnedHeaders = headerGroup.headers.filter((header) =>
      isPinnedTableHeader(header),
    );
    const scrollableHeaders = headerGroup.headers.filter(
      (header) => !isPinnedTableHeader(header),
    );
    const stickyTop = headerGroupIndex === 0;

    return (
      <Box
        key={headerGroup.id}
        w={`${pinnedWidth + scrollableWidth}px`}
        minW={`${pinnedWidth + scrollableWidth}px`}
        h={`${COVERAGE_HEADER_ROW_HEIGHT}px`}
        bg={bgColor}
        position="sticky"
        top={headerGroupIndex === 0 ? 0 : COVERAGE_HEADER_ROW_HEIGHT}
        zIndex={stickyTop ? 30 : 28}
        display="grid"
        gridTemplateColumns={`${pinnedWidth}px ${scrollableWidth}px`}
        alignItems="stretch"
      >
        <Box
          position="sticky"
          left={0}
          zIndex={stickyTop ? 32 : 30}
          bg={bgColor}
          w={`${pinnedWidth}px`}
          minW={`${pinnedWidth}px`}
          display="flex"
        >
          {pinnedHeaders.map((header) =>
            renderHeaderCell(header, headerGroupIndex),
          )}
        </Box>
        <Box
          position="relative"
          w={`${scrollableWidth}px`}
          minW={`${scrollableWidth}px`}
          h="100%"
        >
          {virtualizeColumns ? (
            virtualColumns.map((virtualColumn) => {
              const header = resolveScrollableHeader(
                scrollableHeaders,
                scrollableColumns,
                virtualColumn.index,
              );
              if (!header) {
                return null;
              }
              return (
                <Box
                  key={header.id}
                  position="absolute"
                  top={0}
                  left={`${virtualColumn.start}px`}
                  w={`${virtualColumn.size}px`}
                  h="100%"
                >
                  {renderHeaderCell(header, headerGroupIndex, virtualColumn)}
                </Box>
              );
            })
          ) : (
            <Flex h="100%">
              {scrollableHeaders.map((header) =>
                renderHeaderCell(header, headerGroupIndex),
              )}
            </Flex>
          )}
        </Box>
      </Box>
    );
  };

  const rowItems = virtualize
    ? rowVirtualizer.getVirtualItems()
    : rows.map((_, index) => ({
        index,
        start: index * COVERAGE_ROW_ESTIMATE,
        size: COVERAGE_ROW_ESTIMATE,
      }));

  const bodyHeight = virtualize
    ? rowVirtualizer.getTotalSize()
    : rows.length * COVERAGE_ROW_ESTIMATE;

  useEffect(() => {
    setLoading(false);
  }, [data, setLoading]);

  return (
    <Box
      ref={tableContainerRef}
      overflow="auto"
      h="100%"
      w="100%"
      role="grid"
      mt="12px"
      mb="24px"
      bg={bgColor}
      className="coverage-data-table"
    >
      <Box minW={`${pinnedWidth + scrollableWidth}px`} bg={bgColor}>
        <Box bg={bgColor}>
          {headerGroups.map((headerGroup, headerGroupIndex) =>
            renderHeaderGroupRow(headerGroup, headerGroupIndex),
          )}
        </Box>
        <Box
          position="relative"
          h={`${bodyHeight}px`}
          minW={`${pinnedWidth + scrollableWidth}px`}
          bg={bgColor}
        >
          {rowItems.map((virtualRow) => {
            const row = rows[virtualRow.index] as Row<ProductCoverage>;
            if (!row) {
              return null;
            }
            return (
              <VirtualGridRow
                key={row.id}
                row={row}
                virtualRow={virtualRow}
                virtualizeRows={virtualize}
                virtualizeColumns={virtualizeColumns}
                virtualColumns={virtualColumns}
                scrollableWidth={scrollableWidth}
                pinnedWidth={pinnedWidth}
                renderCell={renderCell}
              />
            );
          })}
        </Box>
      </Box>
    </Box>
  );
}

export const DetailedDataTable = (props: {
  data: Array<ProductCoverage>;
  chronologyParameter: string;
  virtualize: boolean;
  setLoading: (value: boolean) => void;
  canEditDeviationReason?: boolean;
  getProductGeneralNoteId?: (productId: number) => string | null;
  getProductGeneralNoteLabel?: (productId: number) => string | null;
  onDeviationReasonSaved?: (
    productId: number,
    deviationNoteId: string,
    label: string,
  ) => void;
  canEditPeriodDeviation?: boolean;
  getPeriodDeviationNoteId?: (
    productId: number,
    periodIndex: number,
  ) => string | null;
  getPeriodDeviationNoteLabel?: (
    productId: number,
    periodIndex: number,
  ) => string | null;
  onPeriodDeviationChange?: PeriodDeviationReasonCellProps["onChange"];
}) => {
  const {
    data,
    chronologyParameter,
    virtualize,
    setLoading,
    canEditDeviationReason = false,
    getProductGeneralNoteId,
    getProductGeneralNoteLabel,
    onDeviationReasonSaved,
    canEditPeriodDeviation = false,
    getPeriodDeviationNoteId,
    getPeriodDeviationNoteLabel,
    onPeriodDeviationChange,
  } = props;
  const pinnedFields = DETAILED_PINNED_FIELDS;
  const codeW = 90;
  const descriptionW = 135;
  const sublineW = 100;
  const colMetric = 84;
  const pinnedLimitField = pinnedFields[pinnedFields.length - 1];
  const productTextPinnedColumns = useMemo(
    () =>
      new Set<string>(
        pinnedFields.filter((id) => id !== "current"),
      ),
    [pinnedFields],
  );

  const renderCell = useCallback((cell: Cell<ProductCoverage, any>) => {
    const columnId = String(cell.column.id);
    const motivoAnchor = isMotivoAnchorColumn(columnId);
    const productTextPinned = productTextPinnedColumns.has(columnId);
    const isCurrentColumn = columnId === "current";
    const cellOverflow = isCurrentColumn ? "visible" : "hidden";
    return (
      <Box
        key={cell.id}
        role="gridcell"
        fontSize={{ sm: "14px" }}
        bg={COVERAGE_TABLE_BG}
        px={motivoAnchor || productTextPinned ? 2 : "10px"}
        py={1}
        display="flex"
        alignItems={
          productTextPinned ? "center" : motivoAnchor ? "stretch" : "stretch"
        }
        justifyContent={productTextPinned ? "center" : undefined}
        h="100%"
        w="100%"
        minW={0}
        maxW="100%"
        overflow={cellOverflow}
        boxSizing="border-box"
        boxShadow="none"
        zIndex={isCurrentColumn ? 12 : undefined}
        style={{
          ...getPinnedStickyStyle(cell.column, cell.id),
          ...FLAT_GRID_CELL_STYLE,
        }}
      >
        <Box
          w="100%"
          minW={0}
          maxW="100%"
          h={motivoAnchor ? "100%" : undefined}
          overflow={cellOverflow}
        >
          {flexRender(cell.column.columnDef.cell, cell.getContext())}
        </Box>
      </Box>
    );
  }, [pinnedLimitField, productTextPinnedColumns]);

  const periodCount = data[0]?.coverage?.length ?? 0;

  const columns: ColumnDef<ProductCoverage, any>[] = useMemo(() => [
    helper.group({
      header: "Producto",
      columns: [
        helper.accessor("productProps.erpCode", {
          id: "code",
          header: () => <Header text="Código" />,
          cell: (info: any) => <ProductValue attr={info.getValue()} />,
          size: codeW,
        }),
        helper.accessor("productProps.description", {
          id: "description",
          header: () => <Header text="Descripción" />,
          cell: (info: any) => <ProductValue attr={info.getValue()} />,
          size: descriptionW,
        }),
        helper.accessor("productProps.category", {
          id: "category",
          header: () => <Header text="Categoría" />,
          cell: (info: any) => <ProductValue attr={info.getValue()} />,
        }),
        helper.accessor("productProps.line", {
          id: "line",
          header: () => <Header text="Línea" />,
          cell: (info: any) => <ProductValue attr={info.getValue()} />,
        }),
        helper.accessor("productProps.subLine", {
          id: "subline",
          header: () => <Header text="Sublínea" />,
          cell: (info: any) => <ProductValue attr={info.getValue()} />,
          size: sublineW,
        }),
        helper.accessor((row) => row.productProps.costoFobXUnidad, {
          id: "fob-unidad",
          header: () => <Header text="Costo FOB x Unidad" />,
          cell: (info: any) => (
            <ProductValue attr={formatAttrLabel(info.getValue())} />
          ),
          size: colMetric,
        }),
        helper.accessor((row) => row.productProps.costoFobXBulto, {
          id: "fob-bulto",
          header: () => <Header text="Costo FOB x Bulto" />,
          cell: (info: any) => (
            <ProductValue attr={formatAttrLabel(info.getValue())} />
          ),
          size: colMetric,
        }),
        helper.accessor((row) => row.productProps.unidadXBulto, {
          id: "unidad-bulto",
          header: () => <Header text="Unidad x Bulto" />,
          cell: (info: any) => (
            <ProductValue attr={formatBultoLabel(info.getValue(), 0)} />
          ),
          size: 72,
        }),
        helper.accessor((row) => row.productProps.cbmXBulto, {
          id: "cbm-bulto",
          header: () => <Header text="CBM x Bulto" />,
          cell: (info: any) => (
            <ProductValue attr={formatBultoLabel(info.getValue())} />
          ),
          size: 72,
        }),
        helper.accessor((row) => row.productProps.pesoXBulto, {
          id: "peso-bulto",
          header: () => <Header text="Peso x Bulto" />,
          cell: (info: any) => (
            <ProductValue attr={formatBultoLabel(info.getValue())} />
          ),
          size: 72,
        }),
        helper.display({
          id: "current",
          header: () => <Header text="Actual" />,
          cell: (info) => (
            <ActualColumnCell
              row={info.row.original}
              canEditDeviationReason={canEditDeviationReason}
              getProductGeneralNoteId={getProductGeneralNoteId}
              getProductGeneralNoteLabel={getProductGeneralNoteLabel}
              onDeviationReasonSaved={onDeviationReasonSaved}
            />
          ),
          size: COVERAGE_CURRENT_COLUMN_WIDTH,
        }),
      ],
    }),
    ...Array.from({ length: periodCount }, (_, periodIndex) => {
      const periodHeader = data[0]?.coverage[periodIndex];
      return helper.group({
        id: "chronology-" + periodIndex,
        header: () => (
          <Header
            text={
              periodHeader
                ? getHeader(periodHeader, chronologyParameter)
                : HEADER_DATE_FALLBACK
            }
          />
        ),
        columns: [
          helper.display({
            id: "period-" + periodIndex,
            enableSorting: false,
            header: ({ table }) => (
              <PeriodColumnHeader periodIndex={periodIndex} table={table} />
            ),
            cell: (info) => (
              <PeriodCoverageCell
                period={info.row.original.coverage[periodIndex]}
                periodIndex={periodIndex}
                row={info.row.original}
                chronologyParameter={chronologyParameter}
                canEditPeriodDeviation={canEditPeriodDeviation}
                getPeriodDeviationNoteId={getPeriodDeviationNoteId}
                getPeriodDeviationNoteLabel={getPeriodDeviationNoteLabel}
                onPeriodDeviationChange={onPeriodDeviationChange}
              />
            ),
            size: COVERAGE_PERIOD_COLUMN_WIDTH,
          }),
        ],
      });
    }),
  ], [
    periodCount,
    data,
    chronologyParameter,
    canEditDeviationReason,
    canEditPeriodDeviation,
    getProductGeneralNoteId,
    getProductGeneralNoteLabel,
    onDeviationReasonSaved,
    getPeriodDeviationNoteId,
    getPeriodDeviationNoteLabel,
    onPeriodDeviationChange,
  ]);

  if (periodCount === 0) {
    return null;
  }

  return (
    <DataTable
      data={data}
      virtualize={virtualize}
      pinnedFields={pinnedFields}
      columns={columns}
      renderCell={renderCell}
      scrollableColumnEstimate={COVERAGE_PERIOD_COLUMN_WIDTH}
      setLoading={setLoading}
    />
  )
};

export const HeatmapDataTable = (props: {
  data: Array<ProductCoverage>;
  chronologyParameter: string;
  virtualize: boolean;
  setLoading: (value: boolean) => void;
}) => {
  const { data, chronologyParameter, virtualize, setLoading } = props;
  const pinnedFields = HEATMAP_PINNED_FIELDS;
  const pinnedLimitField = pinnedFields[2];

  const renderCell = useCallback((cell: Cell<ProductCoverage, any>) => {
    const columnId = String(cell.column.id);
    let bg = COVERAGE_TABLE_BG;
    if (columnId.indexOf("days") !== -1) {
      bg = "green.500";
      const val = Number(cell.getValue());
      if (!isNaN(val)) {
        if (val < 120 && val > 0) {
          bg = "orange.300";
        } else if (val > 365) {
          bg = "purple.400";
        } else if (val < 1 && ![-385, -1650].includes(val)) {
          bg = "red.500";
        } else if ([-385, -1650].includes(val)) {
          bg = "blue.500";
        }
      }
    }
    const isPinned = cell.column.getIsPinned() === "left";
    return (
      <Box
        key={cell.id}
        role="gridcell"
        fontSize={{ sm: "14px" }}
        bg={bg}
        px="10px"
        display="flex"
        alignItems="center"
        justifyContent="center"
        h="100%"
        w="100%"
        minW={0}
        maxW="100%"
        overflow="hidden"
        boxSizing="border-box"
        boxShadow="none"
        style={{
          ...getPinnedStickyStyle(cell.column, cell.id),
          ...FLAT_GRID_CELL_STYLE,
        }}
      >
        <Box w="100%" minW={0} textAlign="center" overflow="hidden">
          {flexRender(cell.column.columnDef.cell, cell.getContext())}
        </Box>
      </Box>
    );
  }, [pinnedLimitField]);

  const periodCount = data[0]?.coverage?.length ?? 0;

  const columns: ColumnDef<ProductCoverage, any>[] = useMemo(() => [
    helper.group({
      header: "Producto",
      columns: [
        helper.accessor("productProps.erpCode", {
          id: "code",
          header: () => <Header text="Código" />,
          cell: (info: any) => <ProductValue attr={info.getValue()} />,
          size: 115,
        }),
        helper.accessor("productProps.description", {
          id: "description",
          header: () => <Header text="Descripción" />,
          cell: (info: any) => <ProductValue attr={info.getValue()} />,
          size: 150,
        }),
        helper.accessor("productProps.subLine", {
          id: "subline",
          header: () => <Header text="Sublínea" />,
          cell: (info: any) => <ProductValue attr={info.getValue()} />,
          size: 105,
        }),
      ],
    }),
    ...Array.from({ length: periodCount }, (_, periodIndex) => {
      const periodHeader = data[0]?.coverage[periodIndex];
      return helper.group({
        header: periodHeader
          ? getHeader(periodHeader, chronologyParameter, true)
          : HEADER_DATE_FALLBACK,
        columns: [
          helper.accessor(
            (row) => row.coverage[periodIndex]?.coverageTime ?? null,
            {
              id: "days-" + periodIndex,
              header: () => <Header text="Días" />,
              cell: (info: any) => (
                <CoverageValue
                  value={info.getValue()}
                  textProps={{
                    fontSize: { base: "14px", lg: "md" },
                    fontWeight: "700",
                  }}
                />
              ),
              size: 120,
              maxSize: 120,
            },
          ),
        ],
      });
    }),
  ], [chronologyParameter, data, periodCount]);

  if (periodCount === 0) {
    return null;
  }

  return (
    <DataTable
      data={data}
      virtualize={virtualize}
      pinnedFields={pinnedFields}
      columns={columns}
      renderCell={renderCell}
      scrollableColumnEstimate={120}
      setLoading={setLoading}
    />
  )
};